import {
  Avatar,
  Box,
  Divider,
  Grid,
  GridItem,
  Heading,
  Icon,
  Image,
  Link,
  Skeleton,
  Text,
  VStack
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { DetailSheet } from '../../../components/DetailSheet'
import { FormModal } from '../../../components/FormModal'
import { SectionTitle } from '../../../components/SectionCard'
import ShortText from '../../../components/ShortText'
import { EXHIBITION_STATUS, StatusBadge } from '../../../components/StatusBadge'
import { formatApiDate, formatCurrency, formatDateTime, useAppToast, formatPhone } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import { IContestApplicationComplete, MANUAL_PAYMENT_TYPE_LABELS } from '../../../services/Contests/interfaces'
import { resolveArtMeta } from '../Contests/artFieldHelpers'

interface IContestApplicationModalProps {
  isOpen: boolean
  onClose: () => void
  applicationId: number
}

export default function ContestApplicationModal({ isOpen, onClose, applicationId }: IContestApplicationModalProps) {
  const contestService = useMemo(() => new ContestsService(), [])
  const [application, setApplication] = useState<IContestApplicationComplete | null>(null)
  const toast = useAppToast()

  useEffect(() => {
    if (!isOpen) {
      setApplication(null)
      return
    }

    contestService
      .getApplicationDetails(applicationId)
      .then((response) => {
        setApplication(response)
      })
      .catch(() => {
        toast.error('Erro ao Buscar Informações da Inscrição!')
      })
  }, [isOpen])

  return (
    <FormModal isOpen={isOpen} onClose={onClose} size="wide">
      {/* Header Section with Profile */}
      <VStack spacing={0} bg="gray.800" pt={8} pb={6} position="relative">
        <Skeleton isLoaded={!!application} borderRadius="full">
          <Avatar
            size="2xl"
            name={application?.applicant.name}
            src={application?.applicant.image_url || application?.applicant.image_path}
            border="4px solid"
            borderColor="white"
            shadow="xl"
          />
        </Skeleton>
        <Skeleton isLoaded={!!application} mt={4} minH="36px" minW="260px">
          <Heading fontWeight="bold" color="white" textAlign="center" mt={4}>
            {application?.applicant.name || '-'}
          </Heading>
        </Skeleton>
        <Skeleton isLoaded={!!application} mt={2} minH="24px" minW="200px">
          <Text color="gray.300" fontSize="md">
            {application?.contest.name || '-'}
          </Text>
        </Skeleton>
        <Skeleton isLoaded={!!application} mt={1} minH="21px" minW="220px">
          <Text variant="caption">
            Inscrito em: {application?.created_at ? formatDateTime(application.created_at) : '-'}
          </Text>
        </Skeleton>
        {application?.created_by && (
          <Text variant="caption" mt={1}>
            Inscrição manual por {application.created_by.name}
          </Text>
        )}
      </VStack>

      {/* Information Section */}
      <Box p={6}>
        <DetailSheet
          layout="grid"
          isLoading={!application}
          mb={6}
          fields={[
            { label: 'CPF', value: application?.applicant.cpf },
            { label: 'E-mail', value: application?.applicant.email },
            { label: 'Telefone', value: formatPhone(application?.applicant.phone) },
            {
              label: 'Instagram',
              value: application?.applicant.instagram && (
                <Link
                  href={`https://instagram.com/${application.applicant.instagram}`}
                  isExternal
                  color="pink.500"
                  fontWeight="medium"
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon viewBox="0 0 24 24" boxSize={4}>
                    <path
                      fill="currentColor"
                      d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"
                    />
                  </Icon>
                  @{application.applicant.instagram}
                </Link>
              ),
              emptyValue: 'Não informado'
            },
            { label: 'Curso', value: application?.course.name },
            { label: 'Professor', value: application?.teacher.name }
          ]}
        />

        {/* Manual Payment Section (ABRA-398) */}
        {application?.payment?.method === 'manual' && (
          <>
            <Divider my={6} />
            <SectionTitle>Pagamento na recepção</SectionTitle>
            <DetailSheet
              layout="grid"
              mb={6}
              fields={[
                {
                  label: 'Forma de pagamento',
                  value:
                    application.payment.manual_payment_type &&
                    (MANUAL_PAYMENT_TYPE_LABELS[application.payment.manual_payment_type] ??
                      application.payment.manual_payment_type)
                },
                { label: 'Valor', value: formatCurrency(Number(application.payment.amount)) },
                { label: 'Data do pagamento', value: formatApiDate(application.payment.paid_at) },
                application.payment.notes && { label: 'Observação', value: application.payment.notes, colSpan: 2 }
              ]}
            />
          </>
        )}

        {/* Geolocation Section */}
        {(application?.applicant.geo_ip || application?.applicant.geo_has_info) && (
          <>
            <Divider my={6} />
            <SectionTitle>Localização</SectionTitle>
            <DetailSheet
              layout="grid"
              mb={6}
              fields={[
                application.applicant.geo_ip && {
                  label: 'Endereço IP',
                  value: application.applicant.geo_ip,
                  colSpan: 2
                },
                application.applicant.geo_has_info && { label: 'Cidade', value: application.applicant.geo_city },
                application.applicant.geo_has_info && { label: 'Estado', value: application.applicant.geo_state },
                application.applicant.geo_has_info && { label: 'Latitude', value: application.applicant.geo_latitude },
                application.applicant.geo_has_info && { label: 'Longitude', value: application.applicant.geo_longitude }
              ]}
            />
          </>
        )}

        <Divider my={6} />

        {/* Bank Details Section */}
        {application?.applicant.bank_detail_has_info && (
          <>
            <SectionTitle>Dados Bancários</SectionTitle>
            <DetailSheet
              layout="grid"
              mb={6}
              fields={[
                application.applicant.bank_detail_name && {
                  label: 'Nome do Titular',
                  value: application.applicant.bank_detail_name
                },
                application.applicant.bank_detail_cpf_cnpj && {
                  label: 'CPF / CNPJ',
                  value: application.applicant.bank_detail_cpf_cnpj
                },
                (application.applicant.bank_detail_code || application.applicant.bank_detail_full_name) && {
                  label: 'Instituição Financeira',
                  colSpan: 2,
                  value:
                    application.applicant.bank_detail_code && application.applicant.bank_detail_full_name
                      ? `${application.applicant.bank_detail_code} - ${application.applicant.bank_detail_full_name}`
                      : application.applicant.bank_detail_code || application.applicant.bank_detail_full_name
                },
                application.applicant.bank_detail_agency && {
                  label: 'Agência',
                  value: application.applicant.bank_detail_agency
                },
                application.applicant.bank_detail_account && {
                  label: 'Conta',
                  value: application.applicant.bank_detail_account
                },
                application.applicant.bank_detail_pix && {
                  label: 'Chave PIX',
                  value: application.applicant.bank_detail_pix,
                  colSpan: 2
                }
              ]}
            />
            <Divider my={6} />
          </>
        )}

        {/* Arts Section */}
        <SectionTitle>Obras Submetidas</SectionTitle>

        {!application ? (
          <VStack spacing={4}>
            {[1, 2].map((i) => (
              <Skeleton key={i} height="200px" width="100%" borderRadius="lg" />
            ))}
          </VStack>
        ) : application.arts.length > 0 ? (
          <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={6}>
            {application.arts.map((art) => (
              <GridItem key={art.id}>
                <Box
                  borderRadius="lg"
                  overflow="hidden"
                  shadow="md"
                  transition="transform 0.2s"
                  _hover={{ transform: 'scale(1.02)', shadow: 'xl' }}
                  bg="gray.50"
                >
                  <Box position="relative" height="120px" bg="gray.200">
                    <Image src={art.art_image_url} alt={art.name} width="100%" height="100%" objectFit="cover" />
                    <StatusBadge
                      status={art.exhibition_status}
                      statuses={EXHIBITION_STATUS}
                      position="absolute"
                      top={2}
                      right={2}
                      py={1}
                    />
                  </Box>
                  <Box p={4}>
                    <Heading mb={2} noOfLines={1}>
                      {art.name}
                    </Heading>
                    {resolveArtMeta(art).map((meta) => (
                      <Text variant="caption" key={meta.key} mb={1}>
                        <strong>{meta.label}:</strong>{' '}
                        {meta.answered ? (
                          meta.value
                        ) : (
                          <Text as="span" color="gray.400" fontStyle="italic">
                            {meta.value}
                          </Text>
                        )}
                      </Text>
                    ))}
                    {art.work_type && (
                      <Text variant="caption" mb={1}>
                        <strong>Tipo de Trabalho:</strong> {art.work_type}
                      </Text>
                    )}
                    {art.work_description && (
                      <Text variant="caption" mb={1} whiteSpace="pre-wrap">
                        <strong>Descrição da Obra:</strong>{' '}
                        <ShortText text={art.work_description} beforeChars={170} afterChars={0} />
                      </Text>
                    )}
                    <Text variant="caption" mb={1}>
                      <strong>Leilão:</strong>{' '}
                      {art.available_for_auction === 1 ? 'Habilitada' : 'Não disponível para venda'}
                    </Text>
                    {art.available_for_auction === 1 && (
                      <Text variant="caption">
                        <strong>Lance inicial:</strong> {formatCurrency(Number(art.starting_bid))}
                      </Text>
                    )}
                  </Box>
                </Box>
              </GridItem>
            ))}
          </Grid>
        ) : (
          <Text color="gray.500" textAlign="center" py={8}>
            Nenhuma obra submetida ainda.
          </Text>
        )}

        {application &&
          application.arts.length > 0 &&
          (() => {
            const oldestArt = [...application.arts].sort(
              (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            )[0]
            return (
              <>
                <Divider my={6} />
                <Box bg="gray.50" borderRadius="lg" px={5} py={4} borderLeft="4px solid" borderLeftColor="magenta.500">
                  <SectionTitle as="h3" mb={3}>
                    Aceite dos Termos de Retirada
                  </SectionTitle>
                  <DetailSheet
                    layout="grid"
                    columns={{ base: 1, md: 3 }}
                    fields={[
                      { label: 'Nome completo', value: application.applicant.name },
                      { label: 'CPF', value: application.applicant.cpf },
                      {
                        label: 'Data de aceite',
                        // A submissão mais antiga é a data de aceite dos termos.
                        value: formatDateTime(oldestArt.created_at, { showSeconds: true })
                      }
                    ]}
                  />
                </Box>
              </>
            )
          })()}
      </Box>
    </FormModal>
  )
}
